/* eslint-disable no-console */
/**
 * Centralized User Identity Management
 *
 * This module provides a single place to set/clear user identity across all third-party services.
 * When you need to add a new analytics/monitoring service, just add it here.
 *
 * Usage:
 *   import { setUserIdentity, clearUserIdentity } from '@/lib/user-identity';
 *
 *   // On login/profile selection
 *   setUserIdentity({ userId, userAttributes });
 *
 *   // On logout
 *   clearUserIdentity();
 */

import type { ProfilesResponse } from '@/features/user/types';
import { instrumentation } from '@/instrument';
import { analytics } from '@/lib/analytics';

import { getUserAttributesForAnalytics } from '../analytics/hooks/use-analytics';
export interface UserIdentityAttributes {
  email?: string;
  name?: string;
  role?: string;
  courseName?: string;
  grade?: string;
  variantId?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface SetUserIdentityParams {
  userId: string;
  profile?: ProfilesResponse.Profile;
  course?: ProfilesResponse.EnrolledCourse;
  attributes?: UserIdentityAttributes;
}

/**
 * Set user identity across all third-party services
 */
export function setUserIdentity(params: SetUserIdentityParams): void {
  const { userId, profile, course } = params;

  // Set user ID and attributes in Google Analytics
  try {
    analytics.setUserId(userId);
    const analyticsUserAttributes = getUserAttributesForAnalytics(
      profile,
      course
    );
    analytics.setUserProperties(analyticsUserAttributes);
  } catch (error) {
    console.error('[UserIdentity] Failed to set analytics identity:', error);
  }

  instrumentation.setUser({ id: userId });
}

/**
 * Clear user identity across all third-party services
 * Call this on logout or when switching users
 */
export function clearUserIdentity(): void {
  // Clear Google Analytics user
  try {
    analytics.clearUserId();
    analytics.setUserProperties({});
  } catch (error) {
    console.error('[UserIdentity] Failed to clear analytics identity:', error);
  }

  instrumentation.setUser(null);
}

/**
 * Update user attributes without changing the user ID
 * Useful for updating profile information after changes
 */
export function updateUserAttributes(
  profile?: ProfilesResponse.Profile,
  course?: ProfilesResponse.EnrolledCourse
): void {
  const analyticsUserAttributes = getUserAttributesForAnalytics(
    profile,
    course
  );
  // Update Google Analytics
  try {
    analytics.setUserProperties(analyticsUserAttributes);
  } catch (error) {
    console.error(
      '[UserIdentity] Failed to update analytics attributes:',
      error
    );
  }
}
